import { useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useGetFormattedErrorAndTitle, useSubmitTransaction } from "@looksrare/utils";
import { useToast } from "hooks/useToast";
import { Button, ButtonProps } from "uikit";
import { ClaimWinningsModal } from "views/win/userGeneratedRaffles/components/ClaimWinningsModal/ClaimWinningsModal";
import { useClaimRafflePrize, useClaimRafflePrizes } from "views/win/userGeneratedRaffles/hooks/contractHooks";
import { RaffleUnclaimedPrize } from "views/win/userGeneratedRaffles/types";

interface ClaimRafflePrizesButtonProps extends ButtonProps {
  unclaimedPrizes: RaffleUnclaimedPrize[];
}
export const ClaimPrizesButton = ({ unclaimedPrizes, children, ...props }: ClaimRafflePrizesButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const prizesCount = unclaimedPrizes?.length || 0;
  const { t } = useTranslation();

  const claimPrize = useClaimRafflePrize();
  const claimPrizes = useClaimRafflePrizes();

  const { toast } = useToast();
  const getFormattedErrorAndTitle = useGetFormattedErrorAndTitle();

  const handler = useSubmitTransaction({
    onSend: async () => {
      onOpen();
      if (!unclaimedPrizes?.length) {
        return Promise.reject();
      }

      // Claim a single prize (optimized contract call)
      if (prizesCount === 1 && unclaimedPrizes?.[0].winnerIndexes.length === 1) {
        return await claimPrize({
          raffleId: unclaimedPrizes?.[0].raffleOnChainId,
          winnerIndex: unclaimedPrizes?.[0].winnerIndexes[0],
        });
      }

      // Claim multiple prizes
      const claimPrizesCallData = unclaimedPrizes.map((unclaimedPrize) => ({
        raffleId: unclaimedPrize.raffleOnChainId,
        winnerIndices: unclaimedPrize.winnerIndexes,
      }));
      return await claimPrizes(claimPrizesCallData);
    },
    onError: (error: any) => {
      const { title, description } = getFormattedErrorAndTitle(error);
      toast({ title, description, status: "error", dataIdSuffix: "claim-prizes" });
    },
  });

  return (
    <>
      <Button onClick={handler.submitTransaction} isDisabled={isOpen} {...props}>
        {children || t("Claim Now")}
      </Button>

      <ClaimWinningsModal
        isOpen={isOpen}
        onClose={onClose}
        unclaimedPrizes={unclaimedPrizes}
        isTxConfirmed={handler.isTxConfirmed}
        txHash={handler.txResponse}
      />
    </>
  );
};
